// src/pages/ManualMarketing/ManualMarketingGallery/Step2.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { SingleButton } from "../../../assets/components/ButtonGroup";
import {
  SelectCard,
  SelectCardWithSub,
} from "../../../assets/components/SelectGroup";
import { HeaderIconLActionR } from "../../../assets/components/HeaderGroup";
import { useUIState } from "../../../bridges/uiStateContext";
import arrowleft from "../../../assets/icons/Arrow/left.svg";
import { Chip } from "../../../assets/components/Chip";

import AgeSelectSheet from "../../Ads/components/BottomSheet/AgeSelectSheet";
import ChannelSelectSheet from "../../Ads/components/BottomSheet/ChannelSelectSheet";
import EventTitleSheet from "../../Ads/components/BottomSheet/EventTitleSheet";
import AIStyleOptionSheet from "../../Ads/components/BottomSheet/AIStyleOptionSheet";
import useImageListStore from "../../../stores/imageList";
import { useToast } from "../../../hooks/useToast";

// -------------------------------
// 상수 & 헬퍼 (컴포넌트 밖)
// -------------------------------
const THEME_OPTIONS = ["이벤트"];

const norm = (s = "") => String(s).trim().replace(/\s+/g, "").toLowerCase();
const mapAiToTheme = (val) => {
  if (val == null) return null;
  const s = String(val).trim();
  const byCode = { "3": "이벤트" };
  if (byCode[s]) return byCode[s];
  const hit = THEME_OPTIONS.find((t) => norm(t) === norm(s));
  return hit || null;
};

const AGE_LABEL_MAP = {
  "1": "10대",
  "2": "20대",
  "3": "30대",
  "4": "40대",
  "5": "50대",
  "6": "60대 이상",
};

const mapAiAgeCodeToLabel = (aiAge) => {
  if (!aiAge) return "";
  const code = String(aiAge);
  return AGE_LABEL_MAP[code] || "";
};

// aiChannel 코드 → 시트 value
const mapAiChannelCodeToSheetValue = (code) => {
  const c = String(code);
  switch (c) {
    case "1":
      return "카카오톡";
    case "2":
      return "인스타 스토리";
    case "3":
      return "인스타 게시물";
    case "6":
      return "네이버 밴드";
    default:
      return "";
  }
};

const STYLE_LABEL = {
  1: "3D 이미지",
  2: "실사 이미지",
  3: "캐릭터/만화",
  4: "레트로",
  5: "AI모델",
  6: "예술",
};

// 안전한 localStorage getter
const safeGetLS = (key) => {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export default function EventAI2({
  data,
  updateData,
  onBack,
  onNext,
  aiAge,
  aiChannel,
  aiTitle,
  aiStyle,
  exData,
}) {
  const { registerPageBackHandler } = useUIState();
  const { getImageList } = useImageListStore();
  const { showToast } = useToast(); 

  const [initializedByAI, setInitializedByAI] = useState(false);

  // 하드웨어 뒤로가기 → step back
  useEffect(() => {
    if (!registerPageBackHandler) return;
    const unregister = registerPageBackHandler(() => onBack?.());
    return () => unregister();
  }, [onBack, registerPageBackHandler]);

  const ex = exData ?? data;

  // 🔹 localStorage + props 통합 읽기 (로컬 우선)
  const aiAgeLS = safeGetLS("aiAge");
  const aiChannelLS = safeGetLS("aiChannel");
  const aiStyleLS = safeGetLS("aiStyle");
  const aiTitleLS = safeGetLS("aiTitle");

  const aiAgeRaw = aiAge ?? data?.aiAge ?? aiAgeLS ?? null;
  const aiChannelRaw = aiChannel ?? data?.aiChannel ?? aiChannelLS ?? null;
  const aiTitleRaw = aiTitle ?? data?.aiTitle ?? aiTitleLS ?? null;
  const aiStyleRaw = aiStyle ?? data?.aiStyle ?? aiStyleLS ?? null;

  // 🔹 이 매장의 이미지 리스트 (스타일/썸네일 선택에 사용)
  const storeBusinessNumber =
    ex?.store_business_number ||
    (typeof localStorage !== "undefined"
      ? localStorage.store_business_number
      : null);

  const imageManualList = useMemo(() => {
    if (!storeBusinessNumber) return [];
    const list = getImageList(storeBusinessNumber);
    return list || [];
  }, [storeBusinessNumber, getImageList]);

  // ★ age / channel / style 상태
  const [age, setAge] = useState(data.age || "");
  const [channel, setChannel] = useState(data.channel || "");
  const [subChannel, setSubChannel] = useState(data.subChannel || "");
  const [style, setStyle] = useState(data.style || "");
  const [activeSheet, setActiveSheet] = useState(null);
  const openSheet = (key) => setActiveSheet(key);
  const closeSheet = () => setActiveSheet(null);

  // 채널 → 시트 value
  const toSheetChannelValue = (channel, subChannel) => {
    if (!channel) return "";

    if (channel === "카카오톡") return "카카오톡";
    if (channel === "네이버밴드" || channel === "네이버 밴드")
      return "네이버 밴드";

    if (channel === "인스타그램") {
      if (subChannel === "스토리") return "인스타 스토리";
      if (subChannel === "게시물") return "인스타 게시물";
    }
    return "";
  };

  // 시트 value → 채널
  const fromSheetChannelValue = (selected) => {
    if (selected === "카카오톡") {
      return { channel: "카카오톡", subChannel: "" };
    }
    if (selected === "네이버 밴드") {
      return { channel: "네이버 밴드", subChannel: "" };
    }

    if (selected.startsWith("인스타")) {
      return {
        channel: "인스타그램",
        subChannel: selected.includes("스토리") ? "스토리" : "게시물",
      };
    }

    return { channel: selected, subChannel: "" };
  };

  const canReset =
    !!age ||
    !!channel ||
    !!subChannel ||
    !!style ||
    !!data?.customText ||
    (data?.theme && data.theme !== "이벤트");

  // 초기화
  const handleReset = () => {
    setAge("");
    setChannel("");
    setSubChannel("");
    setStyle("");
    setActiveSheet(null);

    updateData({
      age: "",
      channel: "",
      subChannel: "",
      style: "",
      theme: "이벤트",
      customText: "",
      aiStyle: null,
      prompt: null,
    });

    showToast("초기화되었습니다", {
      placement: "bottom",
      bottomOffset: "88px",
    });
  };

  const aiTitleLabelSafe = mapAiToTheme(aiTitleRaw);
  const aiTitleCodeSafe = useMemo(() => {
    if (!aiTitleLabelSafe) return "";
    const idx = THEME_OPTIONS.indexOf(aiTitleLabelSafe);
    return idx >= 0 ? String(idx + 1) : "";
  }, [aiTitleLabelSafe]);

  // "비어있는 것만" AI로 채우는 patch
  const aiAutoPatch = useMemo(() => {
    const patch = {};

    // 1) 연령: 비어있을 때만
    const nextAge = mapAiAgeCodeToLabel(aiAgeRaw);
    if (!age && nextAge) {
      patch.age = nextAge;
      patch.aiAge = aiAgeRaw;
    }

    // 2) 채널: 비어있을 때만(인스타그램인데 subChannel 비어도 비어있음으로 취급)
    const channelMissing = !channel || (channel === "인스타그램" && !subChannel);
    const aiSheet =
      aiChannelRaw != null ? mapAiChannelCodeToSheetValue(aiChannelRaw) : "";

    if (channelMissing && aiSheet) {
      const { channel: nextCh, subChannel: nextSub } =
        fromSheetChannelValue(aiSheet);

      patch.channel = nextCh;
      patch.subChannel = nextSub;
      patch.aiChannel = String(aiChannelRaw);
    }

    // 3) 주제(이벤트): 비어있을 때만
    const themeNow = (data?.theme ?? "").trim();
    if (!themeNow && aiTitleLabelSafe) {
      patch.theme = aiTitleLabelSafe;
      patch.aiTitle = String(aiTitleRaw);
    }

    // 4) 스타일: 비어있을 때만
    const nextStyle = aiStyleRaw != null ? String(aiStyleRaw) : "";
    if (!style && nextStyle) {
      patch.style = nextStyle;
      patch.aiStyle = nextStyle;
    }

    // 5) prompt: "이번에 스타일을 AI로 채운 경우" + "prompt가 비어있는 경우"만
    const promptNow = (data?.prompt ?? "").trim();
    if (!promptNow && patch.style) {
      const candidates = imageManualList.filter(
        (img) => String(img.design_id) === String(patch.style)
      );
      if (candidates.length > 0) {
        const randomImg = candidates[Math.floor(Math.random() * candidates.length)];
        patch.prompt = randomImg.prompt || "";
      }
    }

    return patch;
  }, [age, channel, subChannel, style, data?.theme, data?.prompt, aiAgeRaw, aiChannelRaw, aiTitleLabelSafe, aiTitleRaw, aiStyleRaw, imageManualList]);

  // "이미 선택됨" = 이번에 채울 게 없음
  const isAIApplied = Object.keys(aiAutoPatch).length === 0;

  // 비어있는 값만” aiAutoPatch 적용
  const handleAIAutoSelect = useCallback(() => {
    if (Object.keys(aiAutoPatch).length === 0) return;

    if (aiAutoPatch.age != null) setAge(aiAutoPatch.age);

    if (aiAutoPatch.channel != null) {
      setChannel(aiAutoPatch.channel);
      setSubChannel(aiAutoPatch.subChannel || "");
    }

    if (aiAutoPatch.style != null) {
      const s = String(aiAutoPatch.style);
      setStyle(s);

      try {
        if (typeof localStorage !== "undefined") localStorage.setItem("aiStyle", s);
      } catch (e) {
        console.warn("aiStyle localStorage set 실패", e);
      }
    }

    updateData(aiAutoPatch);
  }, [aiAutoPatch, updateData]);

  // "기본값을 ai 선택값으로 설정" 기능
  useEffect(() => {
    if (initializedByAI) return;

    const hasUserSelection =
      !!data?.age || !!data?.channel || !!data?.style;

    const hasAI =
      aiAgeRaw != null ||
      aiChannelRaw != null ||
      aiTitleRaw != null ||
      aiStyleRaw != null;

    if (!hasUserSelection && hasAI) {
      handleAIAutoSelect();
      setInitializedByAI(true);
    }
  }, [
    initializedByAI,
    data?.age,
    data?.channel,
    data?.style,
    aiAgeRaw,
    aiChannelRaw,
    aiTitleRaw,
    aiStyleRaw,
    handleAIAutoSelect,
  ]);

  const isChannelValid =
    !!channel && (channel !== "인스타그램" || !!subChannel);

  const canGoNext = !!(age && isChannelValid && style);

  const handleNext = () => {
    if (!canGoNext) return;

    const patch = {
      age,
      channel,
      subChannel: channel === "인스타그램" ? subChannel : "",
      style,
      aiAge: aiAgeRaw ?? null,
      aiChannel: aiChannelRaw ?? null,
      // aiStyle / aiThumbnail 은 이미 updateData 에 반영돼 있음
    };

    updateData(patch);
    onNext?.(patch);
  };

  return (
    <div className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-surface-default">
      <HeaderIconLActionR
        title=""
        actionText="초기화"
        onAction={canReset ? handleReset : undefined}
        icon={arrowleft}
        onIconClick={onBack}
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-[16px] [touch-action:pan-y] [-webkit-overflow-scrolling:touch]">
        <div className="flex items-center justify-between pb-[32px]">
          <h2 className="text-t2">옵션 설정</h2>
          <Chip
            chip="d"
            label="AI 자동 선택"
            status={"selected"}
            onClick={() => {
              if (isAIApplied) {
                showToast("이미 선택되어 있습니다", {
                  placement: "bottom",
                  bottomOffset: "88px",
                });
              } else {
                handleAIAutoSelect();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-[16px]">
          <SelectCard
            label="홍보 고객"
            value={age}
            placeholder="연령대 선택"
            onClick={() => openSheet("age")}
          />

          <SelectCard
            label="홍보 채널"
            value={
              channel === "인스타그램"
                ? subChannel
                  ? `인스타 ${subChannel}`
                  : "인스타그램"
                : channel
            }
            placeholder="채널 선택"
            onClick={() => openSheet("channel")}
          />

          <SelectCardWithSub
            label="이벤트 내용"
            value={data.theme === "이벤트" ? "" : data.theme || ""}
            subLabel={data.customText || data.aiTitleText || ""}
            subPlaceHolder="작성한 내용이 없습니다"
            placeholder=" "
            onClick={() => openSheet("topic")}
          />

          <SelectCard
            label="이미지 스타일"
            value={STYLE_LABEL[String(style)] || ""}
            placeholder="스타일 선택"
            onClick={() => openSheet("style")}
          />
        </div>
      </div>

      <AgeSelectSheet
        isOpen={activeSheet === "age"}
        onClose={closeSheet}
        value={age}
        aiAge={aiAgeRaw}
        onConfirm={(nextAge) => {
          setAge(nextAge);
          updateData({ age: nextAge });
        }}
      />

      <ChannelSelectSheet
        isOpen={activeSheet === "channel"}
        onClose={closeSheet}
        value={toSheetChannelValue(channel, subChannel)}
        onConfirm={(selected) => {
          const { channel: nextChannel, subChannel: nextSub } =
            fromSheetChannelValue(selected);
          setChannel(nextChannel);
          setSubChannel(nextSub);
          updateData({ channel: nextChannel, subChannel: nextSub });
        }}
        height="h-[702px]"
      />

      <EventTitleSheet
        isOpen={activeSheet === "topic"}
        onClose={closeSheet}
        value={data.theme}
        aiTitle={aiTitleLabelSafe}
        aiReco={aiTitleCodeSafe}
        detailValue={data.customText || data.aiTitleText || ""}
        data={data}
        exData={ex}
        updateData={updateData}
        onConfirm={({ theme, customText }) => {
          updateData({ theme, customText });
          closeSheet();
        }}
        height="h-[702px]"
      />

      {/* 🔻 이미지 스타일 (스타일 + prompt 선택) */}
      <AIStyleOptionSheet
        isOpen={activeSheet === "style"}
        onClose={closeSheet}
        exData={data} // 여기 안에 aiThumbnail 들어 있음
        value={style}
        aiStyle={aiStyleRaw}
        updateData={(patch) => {
          updateData(patch);
          if (patch?.style != null) setStyle(String(patch.style));
        }}
        onConfirm={({ style: pickedStyle, prompt }) => {
          const s = String(pickedStyle);
          setStyle(s);
          updateData({
            style: s,
            ...(prompt ? { prompt } : {}),
          });
          closeSheet();
        }}
        height="h-[720px]"
      />

      <div
        className="shrink-0 border-t border-border-subtle bg-surface-default/95 backdrop-blur-[8px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <SingleButton type="fill" disabled={!canGoNext} onClick={handleNext}>
          이미지 생성하기
        </SingleButton>
      </div>
    </div>
  );
}
